import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

export type UserRole = 'admin' | 'moderator' | 'user';

export const useUserRole = () => {
  const [roles, setRoles] = useState<UserRole[]>([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const checkUserRoles = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        setRoles([]);
        setIsAdmin(false);
        return;
      }

      // Get user roles
      const { data: userRoles, error: rolesError } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user.id);

      if (rolesError) {
        console.error('Error fetching user roles:', rolesError);
        setError('Failed to fetch user roles');
        return;
      }

      const rolesList = userRoles?.map(r => r.role as UserRole) || [];
      setRoles(rolesList);
      setIsAdmin(rolesList.includes('admin'));
      
    } catch (err) {
      console.error('Error checking user roles:', err);
      setError('Failed to check user roles');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkUserRoles();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(() => {
      checkUserRoles();
    });

    return () => subscription.unsubscribe();
  }, []);

  return {
    roles,
    isAdmin,
    loading,
    error,
    refetch: checkUserRoles
  };
};